/**
 * Security policy and resource budgets (§30, §33).
 *
 * §30 lists a set of things that must never happen when a document is viewed:
 * macros, scripts, embedded executables, external application calls, external
 * data connections and remote content. Those are encoded as literal types
 * pinned to `'blocked'` rather than booleans — there is no assignment that
 * turns any of them off, so "disable this for one format" cannot be written
 * even by accident.
 *
 * The resource budgets are the other half. A viewer that faithfully refuses to
 * execute a macro but then dies decoding a 40000×40000 PNG has still failed the
 * user, so every limit that protects against exhaustion lives here too, scaled
 * per platform tier.
 */
import type { FormatSpec } from '../formats/support.js';
import type { DetectionResult } from '../detect/detect.js';
import type { ZipDirectory } from '../detect/zip-directory.js';

/**
 * Capabilities that are refused unconditionally.
 *
 * Every field is the literal `'blocked'`. This is deliberate: §30 admits no
 * per-format exception, and a `boolean` would invite one.
 */
export interface ExecutionPolicy {
  /** VBA and other document macros (§15, §30). Preserved on save, never run. */
  readonly macros: 'blocked';
  /** JavaScript in PDF, HTML, SVG and EPUB (§22, §30). */
  readonly scripts: 'blocked';
  /** PDF /Launch, OLE verbs, protocol handlers (§30). */
  readonly externalApplications: 'blocked';
  /** Spreadsheet web queries, external workbook references (§15, §30). */
  readonly externalDataConnections: 'blocked';
  /** Remote images, fonts, stylesheets, iframes — also a tracking vector (§29). */
  readonly remoteContent: 'blocked';
  /** Executables embedded as attachments or archive entries (§21, §30). */
  readonly embeddedExecutables: 'blocked';
  /** XML external entities and DTD-driven expansion (§30). */
  readonly xmlExternalEntities: 'blocked';
}

export const EXECUTION_POLICY: ExecutionPolicy = Object.freeze({
  macros: 'blocked',
  scripts: 'blocked',
  externalApplications: 'blocked',
  externalDataConnections: 'blocked',
  remoteContent: 'blocked',
  embeddedExecutables: 'blocked',
  xmlExternalEntities: 'blocked',
});

/** Device class the limits are scaled for (§33 "Low-end device support"). */
export type PlatformTier = 'mobile-low' | 'mobile' | 'desktop' | 'web';

export interface ResourceLimits {
  /** Refuse to open beyond this; §34 explains why rather than crashing. */
  readonly maxFileBytes: number;
  /** Decoded pixel budget — the real memory cost of an image, not its file size. */
  readonly maxDecodedPixels: number;
  readonly maxArchiveEntries: number;
  readonly maxArchiveExpansionBytes: number;
  readonly maxCompressionRatio: number;
  readonly maxArchiveNestingDepth: number;
  readonly maxXmlDepth: number;
  readonly maxXmlEntityExpansions: number;
  readonly maxJsonDepth: number;
  /** Beyond this, text opens in a read-only virtualised view. */
  readonly maxEditableTextBytes: number;
  readonly maxSpreadsheetCells: number;
  /** Parsing is cancelled past this; §33 requires responsive cancellation. */
  readonly parseTimeoutMs: number;
}

/**
 * Budgets per tier.
 *
 * The mobile numbers assume the app may be killed for exceeding them, so they
 * sit well below the device's nominal RAM. Desktop is generous because a
 * failure there costs a slow load, not a terminated process.
 */
export const LIMITS_BY_TIER: Readonly<Record<PlatformTier, ResourceLimits>> = Object.freeze({
  'mobile-low': {
    maxFileBytes: 64 * 1024 * 1024,
    maxDecodedPixels: 24_000_000,
    maxArchiveEntries: 2_000,
    maxArchiveExpansionBytes: 256 * 1024 * 1024,
    maxCompressionRatio: 150,
    maxArchiveNestingDepth: 2,
    maxXmlDepth: 256,
    maxXmlEntityExpansions: 1_000,
    maxJsonDepth: 128,
    maxEditableTextBytes: 4 * 1024 * 1024,
    maxSpreadsheetCells: 250_000,
    parseTimeoutMs: 20_000,
  },
  mobile: {
    maxFileBytes: 256 * 1024 * 1024,
    maxDecodedPixels: 80_000_000,
    maxArchiveEntries: 5_000,
    maxArchiveExpansionBytes: 1024 * 1024 * 1024,
    maxCompressionRatio: 200,
    maxArchiveNestingDepth: 3,
    maxXmlDepth: 512,
    maxXmlEntityExpansions: 5_000,
    maxJsonDepth: 256,
    maxEditableTextBytes: 16 * 1024 * 1024,
    maxSpreadsheetCells: 1_000_000,
    parseTimeoutMs: 30_000,
  },
  web: {
    // Browsers cap a single ArrayBuffer well below the desktop shell's reach,
    // and a tab that exhausts memory takes the document with it.
    maxFileBytes: 512 * 1024 * 1024,
    maxDecodedPixels: 100_000_000,
    maxArchiveEntries: 10_000,
    maxArchiveExpansionBytes: 2 * 1024 * 1024 * 1024,
    maxCompressionRatio: 200,
    maxArchiveNestingDepth: 3,
    maxXmlDepth: 1_024,
    maxXmlEntityExpansions: 10_000,
    maxJsonDepth: 512,
    maxEditableTextBytes: 32 * 1024 * 1024,
    maxSpreadsheetCells: 2_000_000,
    parseTimeoutMs: 60_000,
  },
  desktop: {
    maxFileBytes: 4 * 1024 * 1024 * 1024,
    maxDecodedPixels: 400_000_000,
    maxArchiveEntries: 50_000,
    maxArchiveExpansionBytes: 16 * 1024 * 1024 * 1024,
    maxCompressionRatio: 300,
    maxArchiveNestingDepth: 5,
    maxXmlDepth: 4_096,
    maxXmlEntityExpansions: 50_000,
    maxJsonDepth: 2_048,
    maxEditableTextBytes: 256 * 1024 * 1024,
    maxSpreadsheetCells: 10_000_000,
    parseTimeoutMs: 120_000,
  },
});

/** A capability present in the file that we are refusing to honour (§31). */
export interface BlockedFeature {
  readonly kind:
  | 'macros' | 'scripts' | 'external-data' | 'remote-content'
  | 'embedded-executable' | 'external-entities' | 'launch-action'
  /** An archive whose contents exceed a budget: listable, not extractable. */
  | 'oversized-archive';
  /** i18n key for the user-facing explanation (§25). */
  readonly messageKey: string;
  readonly detail: string;
}

/** A reason the file cannot be opened at all. */
export interface Refusal {
  readonly kind:
  | 'too-large' | 'archive-unsafe' | 'archive-too-many-entries'
  | 'archive-expansion-too-large' | 'encrypted-unsupported' | 'empty';
  readonly messageKey: string;
  readonly detail: string;
}

export interface SecurityAssessment {
  readonly tier: PlatformTier;
  readonly limits: ResourceLimits;
  readonly execution: ExecutionPolicy;
  readonly blockedFeatures: readonly BlockedFeature[];
  readonly refusals: readonly Refusal[];
  /** False when a refusal applies; the UI shows the §34 message instead. */
  readonly canOpen: boolean;
  /**
   * True when this file must never be offered for editing, independent of the
   * catalog's edit level — encrypted containers and unresolved formats.
   */
  readonly forceReadOnly: boolean;
  /** §22/§29 — keep out of logs, thumbnails, recents and crash reports. */
  readonly sensitive: boolean;
}

export interface AssessmentInput {
  readonly detection: DetectionResult;
  readonly spec: FormatSpec | null;
  readonly sizeBytes: number;
  readonly tier: PlatformTier;
  /** Present for ZIP-based formats; carries the §21 violations. */
  readonly archive?: ZipDirectory | undefined;
  /** True when structure inspection found macro storage. */
  readonly macrosPresent?: boolean | undefined;
  readonly encrypted?: boolean | undefined;
}

/** Entry paths whose extension makes them executable on a target platform. */
const EXECUTABLE_EXTENSIONS = new Set([
  '.exe', '.dll', '.scr', '.com', '.bat', '.cmd', '.ps1', '.msi', '.vbs', '.js',
  '.jse', '.wsf', '.wsh', '.lnk', '.jar', '.app', '.dmg', '.pkg', '.sh', '.apk',
]);

function lastExtension(name: string): string {
  const base = name.split(/[\\/]/).pop() ?? '';
  const dot = base.lastIndexOf('.');
  return dot <= 0 ? '' : base.slice(dot).toLowerCase();
}

/**
 * Decide what may happen to this file.
 *
 * Called once, immediately after detection and before any parser sees the
 * bytes. The result is both the enforcement contract for the handler and the
 * data shown on the §31 file information screen — the user is told exactly
 * what was blocked, using the same values the code acted on.
 */
export function assessFile(input: AssessmentInput): SecurityAssessment {
  const { detection, spec, sizeBytes, tier, archive } = input;
  const limits = LIMITS_BY_TIER[tier];
  const blockedFeatures: BlockedFeature[] = [];
  const refusals: Refusal[] = [];

  if (sizeBytes === 0) {
    refusals.push({
      kind: 'empty', messageKey: 'error.damagedOrIncomplete',
      detail: 'file is zero bytes',
    });
  }

  if (sizeBytes > limits.maxFileBytes) {
    refusals.push({
      kind: 'too-large', messageKey: 'error.unableToRead',
      detail: `${sizeBytes} bytes exceeds the ${tier} limit of ${limits.maxFileBytes}`,
    });
  }

  const macrosPresent = input.macrosPresent
    ?? detection.warnings.some((w) => w.code === 'macros-present');
  if (macrosPresent) {
    blockedFeatures.push({
      kind: 'macros', messageKey: 'detect.warning.macrosBlocked',
      detail: 'Macro storage present. Preserved byte-for-byte on save, never executed (§15, §30).',
    });
  }

  const encrypted = input.encrypted
    ?? detection.warnings.some((w) => w.code === 'encrypted');
  if (encrypted) {
    // §14 handles password-protected PDFs properly; everything else is refused
    // with an explanation rather than half-opened.
    const handled = spec?.passwordSupport === 'handled';
    if (!handled) {
      refusals.push({
        kind: 'encrypted-unsupported', messageKey: 'error.passwordProtected',
        detail: `${spec?.id ?? 'unknown'} encryption is detected but not handled`,
      });
    }
  }

  // Formats whose very nature is executable content (§22, §30).
  if (spec) {
    if (spec.id === 'html' || spec.id === 'svg' || spec.id === 'epub') {
      blockedFeatures.push({
        kind: 'scripts', messageKey: 'detect.warning.macrosBlocked',
        detail: `${spec.id} may contain scripts; sanitised before render and never executed (§22).`,
      });
      blockedFeatures.push({
        kind: 'remote-content', messageKey: 'privacy.noUpload',
        detail: 'Remote references are blocked, which also prevents tracking (§29).',
      });
    }
    if (spec.family === 'spreadsheet') {
      blockedFeatures.push({
        kind: 'external-data', messageKey: 'detect.warning.macrosBlocked',
        detail: 'External workbook links and web queries are never resolved (§15, §30).',
      });
    }
    if (spec.id === 'pdf') {
      blockedFeatures.push({
        kind: 'launch-action', messageKey: 'detect.warning.macrosBlocked',
        detail: '/Launch, /URI and /GoToR actions and embedded JavaScript are disabled (§30).',
      });
    }
    if (spec.family === 'document' || spec.family === 'presentation' || spec.family === 'spreadsheet') {
      blockedFeatures.push({
        kind: 'external-entities', messageKey: 'detect.warning.macrosBlocked',
        detail: 'XML entity resolution is disabled to prevent XXE and expansion attacks (§30).',
      });
    }
  }

  if (archive) {
    /**
     * Whether opening this file means expanding what is inside it.
     *
     * A DOCX is read by inflating its parts, so an entry that expands 10 000×
     * is a reason to refuse the file outright. An archive the user is browsing
     * is read from its central directory — §21 requires listing to expand
     * nothing — so the same entry is something to *report*, and the refusal
     * belongs at the moment someone asks to extract it.
     *
     * Refusing to list would also hide the finding: a person whose archive
     * contains a bomb is better served by being shown it than by a dialog that
     * says only "cannot open".
     */
    const listedNotExpanded = spec?.family === 'archive';

    for (const v of archive.violations) {
      switch (v.kind) {
        case 'unsafe-path':
          // Always a refusal. There is no safe way to offer extraction for an
          // archive that names paths outside the destination (§21).
          refusals.push({
            kind: 'archive-unsafe', messageKey: 'archive.violation.unsafePath',
            detail: `entry "${v.entry}" escapes the extraction root`,
          });
          break;
        case 'too-many-entries':
          if (listedNotExpanded) {
            blockedFeatures.push({
              kind: 'oversized-archive', messageKey: 'archive.violation.tooManyEntries',
              detail: `${v.count} entries exceeds ${v.limit}; the list is truncated`,
            });
          } else {
            refusals.push({
              kind: 'archive-too-many-entries', messageKey: 'archive.violation.tooManyEntries',
              detail: `${v.count} entries exceeds ${v.limit}`,
            });
          }
          break;
        case 'expansion-too-large':
          if (listedNotExpanded) {
            blockedFeatures.push({
              kind: 'oversized-archive', messageKey: 'archive.violation.expansionTooLarge',
              detail: `${v.bytes} bytes exceeds ${v.limit}; entries are listed but not extracted`,
            });
          } else {
            refusals.push({
              kind: 'archive-expansion-too-large', messageKey: 'archive.violation.expansionTooLarge',
              detail: `${v.bytes} bytes exceeds ${v.limit}`,
            });
          }
          break;
        case 'ratio-too-high':
          if (listedNotExpanded) {
            blockedFeatures.push({
              kind: 'oversized-archive', messageKey: 'archive.violation.ratioTooHigh',
              detail: `entry "${v.entry}" expands ${Math.round(v.ratio)}× (limit ${v.limit}×); extraction of it is refused`,
            });
          } else {
            refusals.push({
              kind: 'archive-expansion-too-large', messageKey: 'archive.violation.ratioTooHigh',
              detail: `entry "${v.entry}" expands ${Math.round(v.ratio)}× (limit ${v.limit}×)`,
            });
          }
          break;
        default:
          break;
      }
    }

    const executables = archive.entries
      .filter((e) => EXECUTABLE_EXTENSIONS.has(lastExtension(e.name)))
      .map((e) => e.name);
    if (executables.length > 0) {
      blockedFeatures.push({
        kind: 'embedded-executable', messageKey: 'archive.masqueradeWarning',
        detail: `executable entries: ${executables.slice(0, 5).join(', ')}`,
      });
    }
  }

  // An unidentified format never reaches an editor. §19: we do not pretend to
  // understand a file well enough to rewrite it.
  const forceReadOnly = encrypted || spec === null || detection.confidence === 'unknown';

  return {
    tier,
    limits,
    execution: EXECUTION_POLICY,
    blockedFeatures,
    refusals,
    canOpen: refusals.length === 0,
    forceReadOnly,
    sensitive: detection.sensitive,
  };
}

/**
 * Guard for image decoding (§33 "Out-of-memory prevention").
 *
 * A 40000×40000 PNG is a few megabytes on disk and 6.4 GB decoded, so the file
 * size limit does not catch it — dimensions must be checked against the budget
 * before allocation, from the header alone.
 */
export function isPixelBudgetExceeded(
  width: number, height: number, limits: ResourceLimits,
): boolean {
  if (!Number.isFinite(width) || !Number.isFinite(height)) return true;
  if (width <= 0 || height <= 0) return true;
  return width * height > limits.maxDecodedPixels;
}

/** Downsample factor that brings an image inside the budget, or 1 if it fits. */
export function downsampleFactor(
  width: number, height: number, limits: ResourceLimits,
): number {
  const pixels = width * height;
  if (pixels <= limits.maxDecodedPixels) return 1;
  return Math.ceil(Math.sqrt(pixels / limits.maxDecodedPixels));
}
